// Motion vocabulary for the signed-out (auth) surface.
//
// Colour deliberately does NOT live here — AuthLayout.module.css is the single
// source for the palette. Nothing in this file animates a colour (only
// transform and opacity), so a JS copy of the hexes would just be a second
// source of truth waiting to drift.
//
// On the palette itself: the auth surface keeps the brand navy #292970 rather
// than importing chat/theme.js, whose own header scopes it to "the chat widget
// and the AI assistant panel". #292970 is the brand (it recurs across App.css
// for .btn-primary, links, headings); #2563eb is a product-surface choice. The
// two share their neutrals — slate ink, the same rose for errors — so they
// still read as one product.
//
// The springs and stagger below are the same values the chat widget uses, so
// the two animated surfaces feel like one product rather than two.
import { useMemo } from "react";
import { useReducedMotion } from "framer-motion";

// Chat's window spring (ChickenChatWidget.jsx:1168). Settles without overshoot.
export const SPRING = { type: "spring", stiffness: 320, damping: 28 };

const FULL = {
  // Entrance cascade. staggerChildren matches the chat list containers (0.05);
  // slightly slower here because the items are larger.
  container: {
    hidden: {},
    visible: { transition: { staggerChildren: 0.06, delayChildren: 0.08 } },
  },
  // The form drives its own stagger rather than inheriting one: it carries
  // AnimationControls for the failed-login shake, and a component with its own
  // `animate` can't be relied on to pass a variant label through to children.
  // delayChildren picks up roughly where the title/subtitle cascade ends, so
  // it still reads as a single continuous sequence.
  formContainer: {
    hidden: {},
    visible: { transition: { staggerChildren: 0.06, delayChildren: 0.2 } },
  },
  item: {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0, transition: SPRING },
  },
  brand: {
    hidden: { opacity: 0, x: -16 },
    visible: { opacity: 1, x: 0, transition: { ...SPRING, damping: 30 } },
  },
  // The whole-page entrance for a public route. Enter-only by design — see
  // the note on PublicRoutes in App.js.
  page: {
    initial: { opacity: 0, y: 8 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.22, ease: "easeOut" } },
    exit: { opacity: 0, y: -8, transition: { duration: 0.16, ease: "easeIn" } },
  },
  shake: { x: [0, -8, 8, -6, 6, 0], transition: { duration: 0.4 } },
  ambient: true,
  reduced: false,
};

// Everything that moves position becomes a plain cross-fade, and the ambient
// shapes stop looping (they still render — they're part of the composition,
// it's the perpetual motion that's the accessibility problem).
const REDUCED = {
  container: { hidden: {}, visible: { transition: { staggerChildren: 0 } } },
  formContainer: { hidden: {}, visible: { transition: { staggerChildren: 0 } } },
  item: { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { duration: 0.2 } } },
  brand: { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { duration: 0.2 } } },
  page: {
    initial: { opacity: 0 },
    animate: { opacity: 1, transition: { duration: 0.15 } },
    exit: { opacity: 0, transition: { duration: 0.1 } },
  },
  // null, not a no-op variant — every call site must guard on it.
  shake: null,
  ambient: false,
  reduced: true,
};

// One hook so AuthLayout and the forms inside it can never disagree about
// whether motion is on.
export const useAuthMotion = () => {
  const reduced = useReducedMotion();
  return useMemo(() => (reduced ? REDUCED : FULL), [reduced]);
};

export default useAuthMotion;
